const ConfigurationFactory = require("./ConfigurationFactory");
const Logger = require("./Logger");
const Platform = require("./Platform");

const service = {};

const _logServiceWorkerEvent = (log) => {
  const { severity, eventDetails, debugInfo } = log;

  if (!severity || !eventDetails) {
    return;
  }

  if (debugInfo) {
    log.debugInfo = JSON.stringify(debugInfo);
  }

  Logger.logServiceWorkerEvent(log);
};

const _onMessageFromSW = (evt) => {
  const message = evt.data || null;

  if (!message) {
    return;
  }

  _logServiceWorkerEvent(message);
};

service.shouldDisableRangeRequests = () => {
  if (ConfigurationFactory.isChromeOSAppPlayer()) {
    return true;
  }

  // Firesticks with browser version <98 are affected by issue 99; and with browser version 98 are affected by issue 130
  if (ConfigurationFactory.isFireOSPlayer() && Platform.getBrowserMajorVersion() < 98) {
    return true;
  }

  return false;
}

const _init = (cb) => {
  const channel = new MessageChannel();

  let inactiveTimerId = null;
  let callbackExecuted = false;

  // Listen for messages on port1
  channel.port1.onmessage = _onMessageFromSW;

  // set a timer to account for SW registration delay or problem
  inactiveTimerId = setTimeout(() => {
    if (!callbackExecuted) {
      cb();
      callbackExecuted = true;
    }
  }, 5000);

  // Register service worker manually
  navigator.serviceWorker.register("./service-worker.js", { scope: "/" })
    .then((registration) => {
      _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "Service worker registered successfully" });

      // Handle service worker updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "New service worker found" });

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "New service worker installed" });
            } else {
              _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "Service worker installed for the very first time" });
            }
          } else if (newWorker.state === "activated") {
            if (navigator.serviceWorker.controller) {
              _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "New service worker activated" });
            } else {
              _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "Service worker activated for the very first time" });
            }
          }
        });
      });

      // Handle controller change
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        _logServiceWorkerEvent({ severity: "DEBUG", eventDetails: "Service worker controller changed" });
      });

      // Wait for the service worker to be ready
      return navigator.serviceWorker.ready;
    })
    .then((registration) => {
      clearTimeout(inactiveTimerId);
      inactiveTimerId = null;

      // Send initialization message to service worker
      if (registration.active) {
        registration.active.postMessage({ message: "INITIALIZE_LOGGING" }, [channel.port2]);

        if (service.shouldDisableRangeRequests()) {
          registration.active.postMessage({ message: "DISABLE_RANGE_REQUESTS" });
        }
      }

      if (!callbackExecuted) {
        cb();
        callbackExecuted = true;
      }

      _unregisterLegacyServiceWorkers();
    })
    .catch((error) => {
      _logServiceWorkerEvent({
        severity: "ERROR",
        eventDetails: "Service worker registration failed",
        eventErrorCode: "E000000095",
        debugInfo: { errorDetails: error.message }
      });

      if (!callbackExecuted) {
        cb();
        callbackExecuted = true;
      }
    });
};

const _unregisterLegacyServiceWorkers = () => {
  navigator.serviceWorker.getRegistrations()
    .then((registrations) => {
      let count = 0;
      for (let i = 0; i < registrations.length; i++) {
        const registration = registrations[i];
        if (registration && registration.scope &&
          (registration.scope.indexOf("https://widgets.risevision.com/stable/templates/") === 0 ||
          registration.scope.indexOf("https://widgets.risevision.com/beta/templates/") === 0 ||
          registration.scope.indexOf("https://widgets.risevision.com/staging/templates/") === 0))
        {
          registration.unregister();
          count++;
        }
      }
      if (count > 0) {
        _logServiceWorkerEvent({ severity: "IMPORTANT", eventDetails: `Unregistered ${count} legacy Service Workers` });
      }
    });
};

const _isSupported = () => {
  return "serviceWorker" in navigator;
};

service.register = (cb) => {
  if (_isSupported()) {
    if (document.domain.indexOf("localhost") === -1) {
      _init(cb);
    } else {
      cb();
    }
  } else {
    _logServiceWorkerEvent({ eventDetails: "Service worker API is not supported", severity: "ERROR", eventErrorCode: "E000000094" });
    cb();
  }
};

module.exports = service;
